import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { getSettings } from "@/lib/api";
import { PrescriptionCustomFields, PrescriptionLayoutConfig } from "@/types/prescription";

export const DEFAULT_PRESCRIPTION_LAYOUT: PrescriptionLayoutConfig = {
  hiddenSections: [],
  customFields: [
    { key: "custom_1", label: "", enabled: false },
    { key: "custom_2", label: "", enabled: false },
    { key: "custom_3", label: "", enabled: false },
  ],
};

// One settings request per doctor/clinic, shared by the Rx pad and every visit card on the page
const settingsCache = new Map<string, ReturnType<typeof getSettings>>();

/** Call after saving Settings (layout, preferences) so the next form or card reads the new values */
export function clearPrescriptionLayoutCache() {
  settingsCache.clear();
}

function loadSettings(doctorId: string, clinicId: string) {
  const key = `${doctorId}|${clinicId}`;
  let p = settingsCache.get(key);
  if (!p) {
    p = getSettings(doctorId, clinicId);
    p.catch(() => settingsCache.delete(key));
    settingsCache.set(key, p);
  }
  return p;
}

const loadLayout = (doctorId: string, clinicId: string) =>
  loadSettings(doctorId, clinicId).then((s): PrescriptionLayoutConfig => s.prescriptionLayout || DEFAULT_PRESCRIPTION_LAYOUT);

/** Settings → Preferences → Auto-save (on unless the doctor turned it off) */
export function useAutoSavePreference() {
  const { currentUser } = useAuth();
  const [autoSave, setAutoSave] = useState(true);
  useEffect(() => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    let cancelled = false;
    loadSettings(currentUser.doctor_id, currentUser.clinic_id)
      .then((s) => { if (!cancelled) setAutoSave(s.preferences?.autoSave !== false && s.preferences?.autoSave !== 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser?.doctor_id, currentUser?.clinic_id]);
  return autoSave;
}

export function usePrescriptionLayout() {
  const { currentUser } = useAuth();
  const [layout, setLayout] = useState<PrescriptionLayoutConfig>(DEFAULT_PRESCRIPTION_LAYOUT);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    loadLayout(currentUser.doctor_id, currentUser.clinic_id)
      .then((l) => {
        if (!cancelled) setLayout(l);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [currentUser?.doctor_id, currentUser?.clinic_id]);

  return { layout, loading };
}

/** A saved prescription's filled custom fields, under the labels set in Settings (a field since switched off keeps its label) */
export function useFilledCustomFields(values: PrescriptionCustomFields | undefined) {
  const { layout } = usePrescriptionLayout();
  return useMemo(() => layout.customFields.flatMap((cf, i) => {
    const value = values?.[cf.key]?.trim();
    return value ? [{ key: cf.key, label: cf.label.trim() || `Custom field ${i + 1}`, value }] : [];
  }), [layout, values]);
}
